import { namedUuid, remToPixel, setCSSVarViewHeight } from '@tvg/fuse-utils';
import FocusTrap from 'focus-trap-react';
import noScroll from 'no-scroll';
import PropTypes from 'prop-types';
import React from 'react';
import { gridConfig as breakpoints } from '../../fuse-responsive-grid/src/grid';
import ModalHeader from './modal-header';
import {
	StyledModal,
	StyledModalBody,
	StyledModalContainer,
	StyledModalGridContainer,
	StyledModalGridItem,
	StyledModalGridRow,
	StyledModalOverlay,
} from './styled-modal';

const { forwardRef, useCallback, useEffect, useMemo, useRef, useState } = React;

const propTypes = {
	children: PropTypes.node.isRequired,
	title: PropTypes.string.isRequired,
	backCallback: PropTypes.func,
	className: PropTypes.string,
	closeCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	focusTrapOptions: PropTypes.object,
	hasBackButton: PropTypes.bool,
	hasPadding: PropTypes.bool,
	headerIcon: PropTypes.element,
	onClose: PropTypes.func,
	open: PropTypes.bool,
	srLabelBackButton: PropTypes.string,
	srLabelCloseButton: PropTypes.string,
	zIndex: PropTypes.string,
};

const defaultProps = {
	backCallback: () => {},
	className: undefined,
	closeCallback: () => {},
	dataTestId: null,
	focusTrapOptions: {},
	hasBackButton: false,
	hasPadding: true,
	headerIcon: undefined,
	onClose: () => {},
	open: false,
	srLabelBackButton: undefined,
	srLabelCloseButton: undefined,
	zIndex: undefined,
};

const Modal = forwardRef(
	(
		{
			children,
			title,
			backCallback,
			className,
			closeCallback,
			dataTestId,
			focusTrapOptions,
			hasBackButton,
			hasPadding,
			headerIcon,
			onClose,
			open,
			srLabelBackButton,
			srLabelCloseButton,
			zIndex,
			...rest
		},
		ref,
	) => {
		// Refs
		const overlayRef = useRef();
		const gridContainerRef = useRef();
		const gridRowRef = useRef();
		// States
		const [activeTrap, setActiveTrap] = useState(false);
		// Unique IDs
		const modalHeadingId = useMemo(() => namedUuid('modal-header-heading'), []);

		// Event handlers
		// Run onClose when Overlay is clicked
		const handleOverlayOnClick = useCallback((event) => {
			if (
				event.target !== overlayRef.current &&
				event.target !== gridContainerRef.current &&
				event.target !== gridRowRef.current
			)
				return;
			onClose();
			closeCallback(event, ref);
		}, []);
		// Run onClose when keypress is escape, otherwise do nothing
		const handleKeyPress = useCallback((event) => {
			if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
				onClose();
				closeCallback(event, ref);
			}
		}, []);

		// Set noScroll and activeTrap only when modal opens and cleanup on close
		useEffect(() => {
			if (open) {
				noScroll.on();
				// Focus trap to be activated after 0.2s due to modal transition time when open
				setTimeout(() => {
					setActiveTrap(true);
				}, 200);
			} else {
				noScroll.off();
				setActiveTrap(false);
			}

			return function cleanup() {
				if (!open) {
					noScroll.off();
					setActiveTrap(false);
				}
			};
		}, [open]);

		// Add key press event listener to window only on initial load
		useEffect(() => {
			const mobileBreakpoint = remToPixel(breakpoints.m.maxWidth);
			const isMobileTabletViewPort = window.innerWidth <= mobileBreakpoint;

			// Add event keyup event listener
			window.addEventListener('keyup', handleKeyPress);
			// Sets the css variable --vh to root element instead of using traditional css vh to mitigate mobile / tablet top and bottom bar issue
			if (isMobileTabletViewPort) setCSSVarViewHeight();

			return function cleanup() {
				// Clean up and remove keyup event listener
				window.removeEventListener('keyup', handleKeyPress);
			};
		}, []);

		return (
			<StyledModal
				className={open ? `fuse-modal--show ${className ?? ``}` : className ?? ``}
				data-testid={dataTestId || 'fuse-modal'}
				open={open}
				ref={ref}
				zIndex={zIndex}
				{...rest}
			>
				<StyledModalOverlay onClick={handleOverlayOnClick} ref={overlayRef}>
					<StyledModalGridContainer ref={gridContainerRef}>
						<StyledModalGridRow ref={gridRowRef}>
							<StyledModalGridItem
								xxs="12"
								m="10"
								l="6"
								colStart-xxs="1"
								colStart-m="2"
								colStart-l="4"
							>
								<FocusTrap
									active={activeTrap}
									focusTrapOptions={{
										allowOutsideClick: true,
										clickOutsideDeactivates: true,
										initialFocus: '.fuse-modal__content',
										...focusTrapOptions,
									}}
								>
									<StyledModalContainer
										aria-modal={open}
										aria-labelledby={modalHeadingId}
										role="dialog"
										tabIndex="-1"
									>
										<ModalHeader
											title={title}
											backCallback={(event) => {
												backCallback(event, ref);
											}}
											closeCallback={(event) => {
												closeCallback(event, ref);
												onClose();
											}}
											hasBackButton={hasBackButton}
											headerIcon={headerIcon}
											headingId={modalHeadingId}
											srLabelBackButton={srLabelBackButton}
											srLabelCloseButton={srLabelCloseButton}
										/>
										<StyledModalBody hasPadding={hasPadding}>{children}</StyledModalBody>
									</StyledModalContainer>
								</FocusTrap>
							</StyledModalGridItem>
						</StyledModalGridRow>
					</StyledModalGridContainer>

					{/* <FocusTrap
						active={activeTrap}
						focusTrapOptions={{
							allowOutsideClick: true,
							clickOutsideDeactivates: true,
							initialFocus: '.fuse-modal__content',
							...focusTrapOptions,
						}}
					>
						<StyledModalContainer
							aria-modal={open}
							aria-labelledby={modalHeadingId}
							role="dialog"
							tabIndex="-1"
						>
							<ModalHeader
								title={title}
								backCallback={(event) => {
									backCallback(event, ref);
								}}
								closeCallback={(event) => {
									closeCallback(event, ref);
									onClose();
								}}
								hasBackButton={hasBackButton}
								headerIcon={headerIcon}
								headingId={modalHeadingId}
								srLabelBackButton={srLabelBackButton}
								srLabelCloseButton={srLabelCloseButton}
							/>
							<StyledModalBody hasPadding={hasPadding}>{children}</StyledModalBody>
						</StyledModalContainer>
					</FocusTrap> */}
				</StyledModalOverlay>
			</StyledModal>
		);
	},
);

Modal.propTypes = propTypes;
Modal.defaultProps = defaultProps;

export { Modal };

export default Modal;
